import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { createContext, destroyContext, domToPng } from 'modern-screenshot'

/**
 * Modalità "cattura schermate" per le segnalazioni.
 *
 * Due strade per lo scatto, in ordine di preferenza:
 *  1. **Fotogramma dallo schermo condiviso** (veloce, ~50 ms): si chiede UNA volta il permesso di
 *     condividere la scheda, poi ogni scatto è un frame preso dallo stream già aperto. È la strada
 *     buona per gli scatti multipli, che era diventata insostenibile con la sola strada 2.
 *  2. **Rendering del DOM** con `modern-screenshot` (lento, da uno a diversi secondi: clona l'albero,
 *     scarica e incorpora font e immagini, poi rasterizza un SVG). Resta come fallback quando il
 *     permesso viene negato o il browser non è in contesto sicuro (HTTP). Il contesto viene creato
 *     UNA volta e riusato tra gli scatti, così il costo di inlining si paga solo al primo.
 *
 * ⚠️ Il rendering del DOM usa `modern-screenshot` (disegna il BROWSER via foreignObject) e
 * NON html2canvas. html2canvas 1.4.1 ha un parser CSS proprio e si rompe su `oklch()` e
 * `color-mix()`, che il tema del portale (Tailwind v4 + PrimeVue) usa a piene mani: ogni scatto
 * falliva, anche su pagine Vue senza contenuti esterni. Se in futuro si valuta un'altra
 * libreria, il criterio è questo: deve far rendere il CSS al browser, non reimplementarlo.
 *
 * Flusso: dal ticket (nuovo o risposta) si attiva la modalità, si naviga liberamente nel
 * portale, si scattano una o più schermate col pulsante nella barra in alto, poi si torna al
 * ticket e gli scatti diventano allegati.
 *
 * Gli scatti restano in memoria (la navigazione SPA non la perde) e vengono anche salvati in
 * sessionStorage come rete di sicurezza per un eventuale ricaricamento della pagina: se la
 * quota non basta si continua solo in memoria, senza rompere nulla.
 */

/** Dove tornare a cattura finita. Nome di route + params, così funziona anche col ticket id. */
export interface RitornoScreenshot {
  name: string
  params?: Record<string, string | number>
  /** Testo del pulsante di rientro, es. "Torna al ticket TCK-2026-00014". */
  etichetta: string
}

/** Massimo di allegati per sessione: allineato al limite del backend (5 per messaggio). */
export const MAX_SCATTI = 5

/**
 * Durata massima di una registrazione, in secondi. Non è un capriccio: il backend accetta
 * allegati fino a 10 MB e un WebM 720p a ~800 kbps sta intorno ai 6 MB al minuto e mezzo.
 * Meglio fermarsi da soli che far scoprire il limite a fine registrazione.
 */
export const MAX_SECONDI_VIDEO = 90

/** Oltre questo tempo il rendering del DOM viene abbandonato con un errore leggibile. */
const TIMEOUT_RENDER_DOM_MS = 20_000

/** True se il browser può registrare lo schermo: serve un contesto sicuro (HTTPS o localhost). */
export function registrazioneDisponibile(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getDisplayMedia
    && typeof MediaRecorder !== 'undefined'
    && window.isSecureContext
}

const CHIAVE_SESSION = 'screenshot-mode'

interface StatoPersistito {
  attivo: boolean
  ritorno: RitornoScreenshot | null
  scatti: string[]
}

export const useScreenshotStore = defineStore('screenshot', () => {
  const attivo = ref(false)
  const ritorno = ref<RitornoScreenshot | null>(null)
  /** Immagini PNG in formato dataURL. */
  const scatti = ref<string[]>([])
  const inCorso = ref(false)

  /**
   * Registrazioni dello schermo. Restano SOLO in memoria: un video pesa megabyte e non entra in
   * sessionStorage, quindi un ricaricamento della pagina le perde (le schermate no).
   */
  const video = ref<Array<{ blob: Blob; nome: string; secondi: number }>>([])
  const registrazioneInCorso = ref(false)
  const secondiRegistrati = ref(0)

  let recorder: MediaRecorder | null = null
  let pezzi: Blob[] = []
  let cronometro: ReturnType<typeof setInterval> | null = null

  /** Stream dello schermo condiviso: aperto al primo scatto veloce e riusato per i successivi. */
  let streamSchermo: MediaStream | null = null
  let videoSorgente: HTMLVideoElement | null = null
  /** true quando l'utente ha negato la condivisione: non gli si richiede a ogni scatto. */
  let condivisioneNegata = false
  /** Contesto di modern-screenshot riusato tra gli scatti (fallback lento). */
  let contestoDom: Awaited<ReturnType<typeof createContext>> | null = null

  const numeroScatti = computed(() => scatti.value.length + video.value.length)
  const pieno = computed(() => numeroScatti.value >= MAX_SCATTI)

  function persisti(): void {
    try {
      const stato: StatoPersistito = {
        attivo: attivo.value,
        ritorno: ritorno.value,
        scatti: scatti.value,
      }
      sessionStorage.setItem(CHIAVE_SESSION, JSON.stringify(stato))
    } catch {
      // Quota superata (le immagini pesano): la modalità resta valida solo in memoria.
    }
  }

  function ripristina(): void {
    try {
      const raw = sessionStorage.getItem(CHIAVE_SESSION)
      if (!raw) return
      const stato = JSON.parse(raw) as StatoPersistito
      attivo.value = stato.attivo ?? false
      ritorno.value = stato.ritorno ?? null
      scatti.value = Array.isArray(stato.scatti) ? stato.scatti : []
    } catch {
      // Stato illeggibile: si riparte pulito.
    }
  }

  /** Entra in modalità cattura, memorizzando dove tornare. */
  function avvia(dest: RitornoScreenshot): void {
    attivo.value = true
    ritorno.value = dest
    scatti.value = []
    persisti()
  }

  /**
   * Fotografa la pagina corrente.
   *
   * Gli elementi marcati `data-screenshot-ignore` (la barra della modalità, il flash, i toast)
   * vengono esclusi dal filtro: si fotografa il portale, non i comandi della cattura.
   */
  async function scatta(): Promise<void> {
    if (pieno.value || inCorso.value) return
    inCorso.value = true
    try {
      const png = (await scattaDaSchermo()) ?? (await scattaDaDom())
      scatti.value = [...scatti.value, png]
      persisti()
    } finally {
      inCorso.value = false
    }
  }

  /**
   * Scatto veloce: un fotogramma dallo stream dello schermo. Il permesso si chiede solo al primo
   * scatto e lo stream resta aperto per quelli successivi (è il caso d'uso: più scatti di fila).
   *
   * @return il PNG, oppure null se questa strada non è disponibile (niente contesto sicuro,
   *         permesso negato, stream chiuso dall'utente) → il chiamante ripiega sul DOM.
   */
  async function scattaDaSchermo(): Promise<string | null> {
    if (condivisioneNegata || !registrazioneDisponibile()) return null
    try {
      if (!streamSchermo || streamSchermo.getVideoTracks().every((t) => t.readyState === 'ended')) {
        streamSchermo = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 5 },
          audio: false,
        })
        // Se l'utente chiude la condivisione dal browser, il prossimo scatto ripiega sul DOM.
        streamSchermo.getVideoTracks()[0]?.addEventListener('ended', () => { chiudiStream() })
        videoSorgente = document.createElement('video')
        videoSorgente.muted = true
        videoSorgente.srcObject = streamSchermo
        await videoSorgente.play()
      }
      const v = videoSorgente
      // Il primo fotogramma può arrivare qualche decina di ms dopo il play: si attende un attimo
      // invece di dichiarare subito il fallimento e ripiegare sul rendering lento.
      for (let i = 0; i < 20 && v && !v.videoWidth; i++) {
        await new Promise((r) => setTimeout(r, 50))
      }
      if (!v || !v.videoWidth) return null
      const canvas = document.createElement('canvas')
      canvas.width = v.videoWidth
      canvas.height = v.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(v, 0, 0)
      return canvas.toDataURL('image/png')
    } catch (e) {
      // NotAllowedError = permesso negato: non lo richiediamo più in questa sessione di cattura.
      if (e instanceof Error && e.name === 'NotAllowedError') condivisioneNegata = true
      chiudiStream()
      return null
    }
  }

  /** Fallback: rendering del DOM. Il contesto si crea una volta e si riusa. */
  async function scattaDaDom(): Promise<string> {
    const opzioni = {
      // scale 1: le schermate di un portale non servono in retina, e restano leggere
      scale: 1,
      backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
      filter: (node: Node) => {
        const el = node as HTMLElement
        return !(el?.dataset && 'screenshotIgnore' in el.dataset)
      },
    }
    if (!contestoDom) {
      contestoDom = await createContext(document.body, opzioni)
    }
    // Timeout: su pagine molto pesanti il rendering del DOM può non arrivare mai a termine, e un
    // pulsante che gira per sempre è peggio di un errore. Meglio dirlo e lasciar riprovare.
    return Promise.race([
      domToPng(contestoDom),
      new Promise<string>((_, reject) => setTimeout(
        () => reject(new Error('La pagina è troppo complessa per essere ridisegnata: '
          + 'concedi la condivisione dello schermo per uno scatto immediato.')),
        TIMEOUT_RENDER_DOM_MS)),
    ])
  }

  function chiudiStream(): void {
    streamSchermo?.getTracks().forEach((t) => t.stop())
    streamSchermo = null
    if (videoSorgente) {
      videoSorgente.srcObject = null
      videoSorgente = null
    }
  }

  /**
   * Avvia la registrazione dello schermo. Il browser chiede all'utente cosa condividere
   * (scheda, finestra o schermo): condividendo LA SCHEDA la registrazione continua anche
   * cambiando pagina, che è il caso utile qui.
   *
   * Si ferma da sola al limite di durata, oppure quando l'utente interrompe la condivisione
   * dal pannello del browser.
   */
  async function avviaRegistrazione(): Promise<void> {
    if (registrazioneInCorso.value || pieno.value) return
    if (!registrazioneDisponibile()) {
      throw new Error('La registrazione dello schermo richiede una connessione sicura (HTTPS).')
    }
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 10 },   // 10 fps: bastano per mostrare un problema, pesano poco
      audio: false,               // nessun audio: eviterebbe di registrare conversazioni in ufficio
    })
    const mime = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4']
      .find((t) => MediaRecorder.isTypeSupported(t))
    recorder = new MediaRecorder(stream, {
      ...(mime ? { mimeType: mime } : {}),
      videoBitsPerSecond: 800_000,
    })
    pezzi = []
    recorder.ondataavailable = (e) => { if (e.data.size > 0) pezzi.push(e.data) }
    recorder.onstop = () => {
      const tipo = recorder?.mimeType?.split(';')[0] ?? 'video/webm'
      const estensione = tipo.includes('mp4') ? 'mp4' : 'webm'
      const blob = new Blob(pezzi, { type: tipo })
      if (blob.size > 0) {
        video.value = [...video.value, {
          blob,
          nome: `registrazione-${video.value.length + 1}.${estensione}`,
          secondi: secondiRegistrati.value,
        }]
      }
      stream.getTracks().forEach((t) => t.stop())
      pezzi = []
      recorder = null
      registrazioneInCorso.value = false
      if (cronometro) { clearInterval(cronometro); cronometro = null }
    }
    // L'utente può interrompere la condivisione dal pannello del browser: chiudiamo pulito.
    stream.getVideoTracks()[0]?.addEventListener('ended', () => { fermaRegistrazione() })

    recorder.start(1000)
    registrazioneInCorso.value = true
    secondiRegistrati.value = 0
    cronometro = setInterval(() => {
      secondiRegistrati.value += 1
      if (secondiRegistrati.value >= MAX_SECONDI_VIDEO) fermaRegistrazione()
    }, 1000)
  }

  /** Ferma la registrazione in corso; il video diventa un allegato in attesa. */
  function fermaRegistrazione(): void {
    if (recorder && recorder.state !== 'inactive') recorder.stop()
  }

  function rimuoviVideo(indice: number): void {
    video.value = video.value.filter((_, i) => i !== indice)
  }

  function rimuovi(indice: number): void {
    scatti.value = scatti.value.filter((_, i) => i !== indice)
    persisti()
  }

  /**
   * Restituisce gli scatti come File pronti da allegare e chiude la modalità.
   * Da chiamare dalla pagina del ticket quando rientra.
   */
  function consuma(prefisso = 'schermata'): File[] {
    const immagini = scatti.value.map((dataUrl, i) => dataUrlToFile(dataUrl, `${prefisso}-${i + 1}.png`))
    const filmati = video.value.map((v) => new File([v.blob], v.nome, { type: v.blob.type }))
    termina()
    return [...immagini, ...filmati]
  }

  /** Esce dalla modalità scartando gli scatti. */
  function annulla(): void {
    termina()
  }

  function termina(): void {
    fermaRegistrazione()
    chiudiStream()
    condivisioneNegata = false
    if (contestoDom) {
      destroyContext(contestoDom)
      contestoDom = null
    }
    attivo.value = false
    ritorno.value = null
    scatti.value = []
    video.value = []
    secondiRegistrati.value = 0
    try {
      sessionStorage.removeItem(CHIAVE_SESSION)
    } catch {
      // niente da fare
    }
  }

  return {
    attivo,
    ritorno,
    scatti,
    inCorso,
    video,
    registrazioneInCorso,
    secondiRegistrati,
    numeroScatti,
    pieno,
    avvia,
    scatta,
    avviaRegistrazione,
    fermaRegistrazione,
    rimuoviVideo,
    rimuovi,
    consuma,
    annulla,
    ripristina,
  }
})

/** dataURL PNG → File, per infilarlo negli allegati come un file scelto a mano. */
function dataUrlToFile(dataUrl: string, nome: string): File {
  const [intestazione, base64] = dataUrl.split(',')
  const mime = /:(.*?);/.exec(intestazione ?? '')?.[1] ?? 'image/png'
  const binario = atob(base64 ?? '')
  const bytes = new Uint8Array(binario.length)
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i)
  return new File([bytes], nome, { type: mime })
}
